import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { Alert } from "~/components/alert";
import { Button } from "~/components/button";
import type { Record } from "~/tools/record";

import { DocLink } from "../react/docLink/DocLink";
import { CloseDrawerButton, DrawerTitle, ExplorationDrawer } from "../react/drawer/ExplorationDrawer";

import type { SetupDashboardDrawerState } from "./_setup-dashboard-drawer-state";
import { SetupDashboardForm } from "./_setup-dashboard-form";

const STATE_TYPE_TO_TITLE = {
	create: "Add New Dashboard",
	edit: "Edit Dashboard Properties",
	clone: "Save Dashboard As...",
} satisfies Record<SetupDashboardDrawerState["type"]>;

const STATE_TYPE_TO_BUTTON_TEXT = {
	create: "CREATE DASHBOARD",
	edit: "UPDATE DASHBOARD",
	clone: "CREATE DASHBOARD",
} satisfies Record<SetupDashboardDrawerState["type"], string>;

const FORM_ID = "setup-dashboard";

export interface SetupDashboardDrawerProps {
	when: undefined | SetupDashboardDrawerState;
}

export const SetupDashboardDrawer: React.FC<SetupDashboardDrawerProps> = ({ when }) => {
	const drawerBaseId = when?.type === "create" ? "dashboard" : when?.id;
	const canSave = when?.type !== "edit" || when.canSave;

	return (
		<ExplorationDrawer
			footer={(state) => <SetupDashboardFooter {...state} showSaveButton={canSave} />}
			headerTitle={({ type }) => <DrawerTitle title={STATE_TYPE_TO_TITLE[type]} />}
			id={drawerBaseId != null && when?.type != null ? `${drawerBaseId}_${when.type}` : undefined}
			layout="compact"
			when={when}
		>
			{(state, close, preservedState) => (
				<div>
					{state.type === "edit" ? (
						<SetupDashboardAlert>You are about to update the properties of the current dashboard.</SetupDashboardAlert>
					) : (
						<SetupDashboardAlert>
							All the dashboards can be found under the <strong className="font-semibold">Dashboards</strong> main menu
							entry.
						</SetupDashboardAlert>
					)}

					<SetupDashboardForm
						canSave={canSave}
						className="mt-6"
						close={close}
						formId={FORM_ID}
						state={preservedState ?? state}
					/>
				</div>
			)}
		</ExplorationDrawer>
	);
};

const SetupDashboardAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Alert>
		{children} To learn more about dashboards, check out{" "}
		<DocLink capture="noop" slug="ui-dashboards">
			our docs
		</DocLink>
		.
	</Alert>
);

const SetupDashboardFooter: React.FC<SetupDashboardDrawerState & { showSaveButton: boolean }> = ({
	form,
	type,
	showSaveButton,
}) => {
	const isSubmitting = useComputed((monitor) => form.isSubmitting(monitor), [form]);

	return (
		<>
			<CloseDrawerButton />
			{showSaveButton && (
				<Button form={FORM_ID} loading={isSubmitting} type="submit" variant="primary">
					{STATE_TYPE_TO_BUTTON_TEXT[type]}
				</Button>
			)}
		</>
	);
};
